{
  "scheduling": "Appointment Scheduling",
  "date": "Date",
  "time": "Time",
  "duration": "Duration",
  "provider": "Provider",
  "reason": "Reason",
  "status": "Status",
  "statusOptions": {
    "scheduled": "Scheduled",
    "confirmed": "Confirmed",
    "arrived": "Arrived",
    "checked-in": "Checked In",
    "in-progress": "In Progress",
    "completed": "Completed",
    "cancelled": "Cancelled",
    "no-show": "No Show"
  },
  "coverageScope": {
    "panel": "Panel",
    "unit": "Unit",
    "facility": "Facility"
  },
  "coverageScopeDescription": {
    "panel": "Covered provider's patient panel only.",
    "unit": "Covered provider's assigned unit.",
    "facility": "Full facility — typically on-call arrangements."
  },
  "dayShort": {
    "0": "Sun",
    "1": "Mon",
    "2": "Tue",
    "3": "Wed",
    "4": "Thu",
    "5": "Fri",
    "6": "Sat"
  },
  "blockType": {
    "available": "Available",
    "blocked": "Blocked"
  },
  "appointmentTypes": {
    "followUp": "Follow-up",
    "newPatient": "New patient",
    "consultation": "Consultation",
    "procedure": "Procedure",
    "urgent": "Urgent",
    "wellnessCheck": "Wellness check",
    "physical": "Physical",
    "telemedicine": "Telemedicine",
    "scheduleTemplate": "Schedule template"
  },
  "pages": {
    "providerDirectory": {
      "title": "Provider Directory",
      "subtitle": "Browse and search healthcare providers",
      "search": {
        "namePlaceholder": "Search by name...",
        "specialtyPlaceholder": "Search by specialty..."
      },
      "filters": {
        "facilityAriaLabel": "Filter by facility",
        "departmentAriaLabel": "Filter by department",
        "allFacilities": "All Facilities",
        "allDepartments": "All Departments"
      },
      "loading": "Loading providers...",
      "loadError": "Failed to load provider directory.",
      "empty": "No providers found matching your criteria.",
      "npiLabel": "NPI:"
    },
    "scheduleTemplateForm": {
      "title": "Create Schedule Template",
      "labels": {
        "provider": "Provider",
        "dayOfWeek": "Day of Week",
        "startTime": "Start Time",
        "endTime": "End Time",
        "slotDuration": "Slot Duration (minutes)",
        "templateName": "Template Name",
        "effectiveStartDate": "Effective From"
      },
      "slotDurationOption": "{{minutes}} minutes",
      "buttons": {
        "cancel": "Cancel",
        "save": "Save Template"
      },
      "toast": {
        "success": "Schedule template saved",
        "failure": "Failed to save schedule template"
      },
      "placeholders": {
        "templateName": "e.g. Monday Morning Clinic"
      },
      "defaultName": "{{day}} schedule"
    },
    "scheduleManagement": {
      "title": "Schedule Management",
      "backToDashboard": "← Dashboard",
      "newTemplate": "New Template",
      "today": "Today",
      "blockTooltip": "{{start}} - {{end}} ({{state}})",
      "slotsLabel": "{{minutes}}min slots",
      "legend": {
        "booked": "Booked"
      },
      "upcomingAppointmentsHeading": "Upcoming Appointments - {{providerName}}",
      "loadingAppointments": "Loading appointments...",
      "unknownPatient": "Unknown Patient",
      "fallbackAppointmentType": "General",
      "noUpcomingAppointments": "No upcoming appointments for this provider"
    },
    "providerCoverage": {
      "title": "Provider Coverage",
      "subtitle": "Time-bounded shift coverage — hand off your patient panel to a colleague for a defined window. Distinct from break-glass; used for vacation, on-call rotations, and planned absences.",
      "actions": {
        "newCoverage": "New Coverage",
        "cancel": "Cancel"
      },
      "form": {
        "title": "New coverage arrangement",
        "labels": {
          "coveredProviderId": "Covered provider",
          "coveringProviderId": "Covering provider",
          "scope": "Scope",
          "from": "From",
          "until": "Until",
          "reason": "Reason"
        },
        "placeholders": {
          "coveredProviderId": "Provider whose panel is being covered",
          "coveringProviderId": "Provider taking over for the window",
          "reason": "e.g. Annual leave 22–29 Apr; on-call weekend cover"
        },
        "help": {
          "coveredProviderId": "Without COVERAGE_ADMIN, this must be yourself.",
          "coveringProviderId": "Search by name and pick — coverage is never granted to a typed ID."
        },
        "aria": {
          "effectiveFromDate": "Effective-from date",
          "effectiveFromTime": "Effective-from time",
          "effectiveUntilDate": "Effective-until date",
          "effectiveUntilTime": "Effective-until time"
        },
        "buttons": {
          "cancel": "Cancel",
          "create": "Create coverage"
        },
        "typeaheadEmpty": "No providers found"
      },
      "sections": {
        "mine": {
          "title": "My Coverage",
          "subtitle": "Coverages where I am the covering or covered provider.",
          "empty": "No coverage on record."
        },
        "all": {
          "title": "All Coverage",
          "subtitle": "Every coverage arrangement in the organization. Admin view.",
          "empty": "No coverage anywhere. That's either very good news or a stale filter."
        }
      },
      "list": {
        "coveringConnector": "covering",
        "groups": {
          "active": "Active ({{count}})",
          "planned": "Planned / Past ({{count}})",
          "revoked": "Revoked ({{count}})"
        },
        "untilRevoked": "until revoked",
        "revokedPrefix": "Revoked",
        "revokeTitle": "Revoke"
      },
      "revokeDialog": {
        "title": "Revoke coverage?",
        "description": "{{coveringProviderName}} will no longer cover {{coveredProviderName}}. The record stays in the audit trail.",
        "confirm": "Revoke",
        "reasonPlaceholder": "Why is this coverage ending? (optional)"
      },
      "toast": {
        "created": "Coverage created",
        "revoked": "Coverage revoked",
        "invalid": "Invalid coverage — check providers and dates",
        "forbidden": "Not allowed to create coverage for this provider",
        "createFailed": "Failed to create coverage",
        "revokeFailed": "Failed to revoke coverage"
      }
    },
    "appointments": {
      "title": "Appointments",
      "subtitle": "Schedule and manage patient appointments",
      "actions": {
        "refresh": "Refresh",
        "schedule": "Schedule Appointment",
        "arriveTitle": "Record arrival",
        "arriveAria": "Record arrival for {{patientName}}",
        "checkInTitle": "Check in",
        "checkInAria": "Check in {{patientName}}",
        "rescheduleTitle": "Reschedule",
        "rescheduleAria": "Reschedule {{patientName}}",
        "cancelTitle": "Cancel",
        "cancelAria": "Cancel {{patientName}} appointment",
        "checkedInLabel": "Checked in",
        "startVisitTitle": "Start visit",
        "startVisitAria": "Start visit for {{patientName}}"
      },
      "weekNav": {
        "previous": "Previous",
        "previousAria": "Previous week",
        "next": "Next",
        "nextAria": "Next week"
      },
      "calendar": {
        "gridAria": "Weekly appointment calendar",
        "timeColumn": "Time",
        "loading": "Loading schedule...",
        "cellAria": "{{dayLabel}} {{date}} at {{time}}",
        "cellTitle": "{{patientName}} - {{appointmentType}} ({{status}})"
      },
      "upcoming": {
        "heading": "Upcoming Appointments",
        "empty": "No appointments found for this week",
        "durationMinutes": "{{minutes}} min",
        "columns": {
          "dateTime": "Date / Time",
          "patient": "Patient",
          "type": "Type",
          "provider": "Provider",
          "duration": "Duration",
          "status": "Status",
          "actions": "Actions"
        }
      },
      "toast": {
        "cancelled": "Appointment cancelled",
        "cancelFailed": "Failed to cancel appointment",
        "visitStarted": "Visit started",
        "startVisitFailed": "Failed to start visit",
        "arrivalRecorded": "Arrival recorded",
        "arrivalFailed": "Failed to record arrival"
      }
    },
    "appointmentForm": {
      "title": {
        "create": "Schedule New Appointment",
        "edit": "Reschedule Appointment"
      },
      "labels": {
        "patient": "Patient",
        "appointmentType": "Appointment Type",
        "provider": "Provider",
        "scheduledAt": "Scheduled at",
        "duration": "Duration",
        "reason": "Reason for Visit"
      },
      "placeholders": {
        "patientSearch": "Search patient by name or MRN...",
        "provider": "Provider name",
        "reason": "Brief reason for visit..."
      },
      "patientTypeahead": {
        "empty": "No patients found"
      },
      "aria": {
        "appointmentDate": "Appointment date",
        "appointmentTime": "Appointment time"
      },
      "durationOption": "{{minutes}} min",
      "buttons": {
        "cancel": "Cancel",
        "schedule": "Schedule Appointment",
        "update": "Update Appointment"
      },
      "toast": {
        "created": "Appointment scheduled successfully",
        "updated": "Appointment updated",
        "createFailed": "Failed to create appointment",
        "updateFailed": "Failed to update appointment"
      },
      "validation": {
        "patientRequired": "Please select a patient",
        "appointmentTypeRequired": "Appointment type is required",
        "scheduledAtRequired": "Date and time are required",
        "durationInvalid": "Duration must be between 1 and 480 minutes",
        "providerRequired": "Please select a provider"
      },
      "providerTypeahead": {
        "empty": "No providers found"
      },
      "loadingEdit": "Loading appointment…",
      "loadEditError": "Could not load this appointment.",
      "patientLocked": "The patient cannot be changed when rescheduling."
    },
    "waitlist": {
      "title": "Appointment Waitlist",
      "backToDashboard": "← Dashboard",
      "entryCount": "{{count}} entries",
      "searchPlaceholder": "Search patient name, MRN, or notes...",
      "clearSearchAria": "Clear search",
      "filters": {
        "allProviders": "All Providers",
        "allTypes": "All Types",
        "providerAriaLabel": "Filter by provider",
        "typeAriaLabel": "Filter by appointment type"
      },
      "columns": {
        "patient": "Patient",
        "provider": "Provider",
        "type": "Type",
        "priority": "Priority",
        "requestedDates": "Requested Dates",
        "dateAdded": "Date Added",
        "notes": "Notes",
        "actions": "Actions",
        "reason": "Reason"
      },
      "priority": {
        "urgent": "Urgent",
        "routine": "Routine",
        "flexible": "Flexible"
      },
      "actions": {
        "scheduleTitle": "Schedule appointment",
        "editPriorityTitle": "Edit priority",
        "removeTitle": "Remove from waitlist"
      },
      "empty": "No waitlist entries match your filters",
      "loadFailed": "Could not load the waitlist. Check your connection and retry.",
      "modal": {
        "title": "Schedule Appointment",
        "closeAria": "Close",
        "providerLabel": "Provider:",
        "requestedLabel": "Requested:",
        "dateTimeLabel": "Appointment Date & Time *",
        "dateAria": "Appointment date",
        "timeAria": "Appointment time",
        "requestedWindow": "Requested window: {{start}} — {{end}}",
        "cancel": "Cancel",
        "submit": "Schedule"
      },
      "toasts": {
        "removed": "Entry removed from waitlist",
        "priorityUpdated": "Priority updated",
        "scheduled": "Appointment scheduled and removed from waitlist",
        "dateTimeRequired": "Please select a date and time",
        "updateFailed": "Failed to update waitlist entry",
        "promoteOrphaned": "The appointment was created but the waitlist entry could not be updated. Check the schedule before retrying."
      }
    },
    "checkIn": {
      "title": "Check in patient",
      "subtitle": "Registration — insurance, authorisation and copay. The patient has already arrived.",
      "fields": {
        "insuranceId": "Insurance",
        "copayAmount": "Copay amount",
        "copayCollected": "Copay collected",
        "authorizationNumber": "Authorisation number",
        "authorizationVerified": "Authorisation verified"
      },
      "copayHint": "Marking a copay collected posts a payment to the patient's account.",
      "submit": "Complete check-in",
      "cancel": "Cancel",
      "toast": {
        "success": "Patient checked in",
        "failed": "Failed to check in patient"
      }
    },
    "providerTypeahead": {
      "loadError": "Provider directory unavailable — check your connection and retry.",
      "retry": "Retry"
    }
  }
}
